import { LoadingButton } from "@mui/lab";
import { Box, DialogTitle, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { RollingNumberType, useUpdateRollingNumberMutation } from "@repo/server-api";
import { RollingNumber } from "@repo/server-api/types";
import { useNotification } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";


export default function DialogRollingNumber(
	props: Readonly<{
		open: boolean;
		setOpen: (value: boolean) => void;
		refreshData: () => void;
		rollingNumber: RollingNumber | null;
	}>,
) {
	const { t } = useTranslation();

	const [open, setOpen] = useState(false);
	const [rollingNumber, setRollingNumber] = useState<RollingNumber | null>(null);

	const [currentValueError, setCurrentValueError] = useState(false);
	const [digitCountError, setDigitCountError] = useState(false);
	const [formatError, setFormatError] = useState(false);
	const [saveClicked, setSaveClicked] = useState<boolean>(false);

	const { showNotification } = useNotification();

	const [updateRollingNumber] = useUpdateRollingNumberMutation({
		variables: {
			contractorId: rollingNumber?.contactId ?? null,
			digitCount: rollingNumber?.digitCount ?? 0,
			format: rollingNumber?.format ?? "",
			type: rollingNumber?.type ?? RollingNumberType.Invoices,
			value: parseInt(rollingNumber?.value ?? "0"),
		},
		onCompleted(data) {
			if (data.updateRollingNumber.boolean) {
				props.refreshData();
				handleClose();
				showNotification(t("contractors.rollingNumber.updated"), "success");
			}
			if (data.updateRollingNumber.errors) {
				data.updateRollingNumber.errors.forEach((error) => {
					if ("message" in error) {
						showNotification(error.message, "error");
						error.errors.forEach((err) => {
							showNotification(err.errorMessage, "error");
						});
					} else {
						showNotification(t("contractors.rollingNumber.unknownError"), "error");
					}
				});
			}
			setSaveClicked(false);
		},
		onError(error) {
			showNotification(error.message, "error");
			setSaveClicked(false);
		},
	});

	useEffect(() => {
		setOpen(props.open);
	}, [props.open]);

	useEffect(() => {
		if (props.rollingNumber) {
			setRollingNumber({
				...props.rollingNumber,
				value: props.rollingNumber.currentValue.toString() ?? "",
			});
		}
	}, [props.rollingNumber]);

	const handleClose = () => {
		setOpen(false);
		setRollingNumber(props.rollingNumber);
		props.setOpen(false);
		setCurrentValueError(false);
		setDigitCountError(false);
		setFormatError(false);
		setSaveClicked(false);
	};

	const handleSubmit = () => {
		// TODO make validation with Yup library
		let valid = true;
		if (!rollingNumber?.value) {
			setCurrentValueError(true);
			valid = false;
		}
		if (!rollingNumber?.digitCount) {
			setDigitCountError(true);
			valid = false;
		}
		if (!rollingNumber?.format) {
			setFormatError(true);
			valid = false;
		}
		if (!valid) {
			return;
		}
		setSaveClicked(true);
		updateRollingNumber();
	};

	return (
		<Dialog
			open={open}
			data-testid="rolling-number-dialog"
			aria-describedby="alert-dialog-description"
			maxWidth="sm"
			fullWidth={true}>
			<DialogTitle>
				{t("number-range") +
					": " +
					t("rollingnumber." + rollingNumber?.type.toLocaleLowerCase(), {
						ns: "enum",
					})}
			</DialogTitle>
			<DialogContent className="flex-col gap-4">
				<Box className="fullCenter">
					<TextField
						data-testid="rolling-number-current-value-input"
						onChange={(event) => {
							setRollingNumber(
								rollingNumber ? { ...rollingNumber, value: event.target.value ?? "0" } : null,
							);
							setCurrentValueError(false);
						}}
						error={currentValueError}
						helperText={currentValueError ? t("required", { ns: "validation" }) : ""}
						className="w-100"
						value={rollingNumber?.value ?? ""}
						label={t("current-value")}
						variant="outlined"
					/>
				</Box>
				<Box className="fullCenter">
					<TextField
						data-testid="rolling-number-digit-count-input"
						onChange={(event) => {
							setRollingNumber(
								rollingNumber
									? {
											...rollingNumber,
											digitCount: parseInt(event.target.value) ?? "",
										}
									: null,
							);
							setDigitCountError(false);
						}}
						error={digitCountError}
						helperText={digitCountError ? t("required", { ns: "validation" }) : ""}
						className="w-100"
						value={rollingNumber?.digitCount ? rollingNumber.digitCount + "" : ""}
						label={t("digit-count")}
						variant="outlined"
					/>
				</Box>
				<Box className="fullCenter">
					<TextField
						data-testid="rolling-number-format-input"
						onChange={(event) => {
							setRollingNumber(
								rollingNumber ? { ...rollingNumber, format: event.target.value ?? "" } : null,
							);
							setFormatError(false);
						}}
						error={formatError}
						helperText={formatError ? t("required", { ns: "validation" }) : ""}
						className="w-100"
						value={rollingNumber?.format ? rollingNumber.format + "" : ""}
						label={t("format")}
						variant="outlined"
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button data-testid="rolling-number-cancel-button" onClick={handleClose} autoFocus>
					{t("cancel")}
				</Button>
				<LoadingButton
					data-testid="rolling-number-save-button"
					loading={saveClicked}
					onClick={handleSubmit}
					className={saveClicked ? "" : "blueButton"}>
					{t("save")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
}
